import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FileDown, ChevronDown, FileSpreadsheet, FileText } from "lucide-react";
import { ActionGridButton } from "../../../../components/ActionButtonGrid";

// One "Download" button in the actions row that asks Excel or PDF.
// The menu is portalled to <body> and positioned from the button: the actions
// row scrolls horizontally on phones (clips an absolute menu) and the filter
// card has a backdrop-filter (traps a position: fixed child inside it).
const DownloadMenuButton = ({ theme, isRTL, t, onExcel, onPdf }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const isDark = theme === "dark";

  const options = [
    onExcel && { key: "excel", label: t("downloadExcel"), icon: FileSpreadsheet, color: "#10b981", onClick: onExcel },
    onPdf && { key: "pdf", label: t("downloadPdf"), icon: FileText, color: "#ef4444", onClick: onPdf },
  ].filter(Boolean);

  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const r = buttonRef.current.getBoundingClientRect();
    setPos({
      top: r.bottom + 6,
      // align the menu with the button's start edge
      ...(isRTL ? { right: window.innerWidth - r.right } : { left: r.left }),
      minWidth: Math.max(r.width, 200),
    });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onDown = (e) => {
      if (!menuRef.current?.contains(e.target) && !buttonRef.current?.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [open]);

  if (options.length === 0) return null;

  return (
    <>
      <ActionGridButton
        buttonRef={buttonRef}
        theme={theme}
        icon={FileDown}
        label={t("download")}
        onClick={toggle}
        active={open}
        aria-haspopup="menu"
        aria-expanded={open}
        trailing={
          <ChevronDown
            size={14}
            style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s ease", opacity: 0.8 }}
          />
        }
      />

      {open && pos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          dir={isRTL ? "rtl" : "ltr"}
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            right: pos.right,
            minWidth: pos.minWidth,
            zIndex: 99980,
            padding: "6px",
            borderRadius: "10px",
            border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
            background: isDark ? "#1e293b" : "#ffffff",
            boxShadow: "0 10px 24px rgba(0,0,0,0.2)",
            display: "flex",
            flexDirection: "column",
            gap: "2px",
          }}
        >
          {options.map((o) => {
            const Icon = o.icon;
            return (
              <button
                key={o.key}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  o.onClick();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  width: "100%",
                  padding: "9px 10px",
                  border: "none",
                  borderRadius: "8px",
                  background: "transparent",
                  color: isDark ? "#f1f5f9" : "#1e293b",
                  fontSize: "13px",
                  fontWeight: 500,
                  textAlign: "start",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <Icon size={16} color={o.color} />
                {o.label}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
};

export default DownloadMenuButton;
